import React from 'react';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { colors } from '../../utils/themeUtils';

/**
 * NoMaxWidthTooltip - A custom tooltip with no maximum width
 * Used across the application for consistent tooltip styling
 */
export const NoMaxWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 'none',
    whiteSpace: 'nowrap',
    backgroundColor: 'rgba(250, 250, 252, 0.98)',
    padding: '10px 14px',
    fontSize: '0.85rem',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(0, 0, 0, 0.06)',
    color: '#333',
    '&.MuiTooltip-tooltipPlacementTop': {
      marginBottom: '6px'
    }
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: 'rgba(250, 250, 252, 0.98)'
  }
});

/**
 * Format tooltip content with consistent styling
 * @param {Object} props - Properties for tooltip content
 * @param {Array} props.items - Array of items to display in tooltip
 * @param {Function} props.formatImpactValue - Function from parent to format impact values
 * @param {Object} props.options - Additional options passed to formatImpactValue
 */
export const TooltipContent = ({ items, formatImpactValue, options = {} }) => {
  // Helper function to capitalize the first letter of a string
  const capitalizeFirstLetter = (string) => {
    if (!string || typeof string !== "string") return "N/A";
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  return (
    <div>
      {items.map((item, i) => (
        <React.Fragment key={i}>
          <span style={{ color: '#5e6c84' }}>
            {capitalizeFirstLetter(item.category)},{' '}
          </span>
          <span style={{ color: '#5e6c84' }}>
            {capitalizeFirstLetter(item.subject_group)},{' '}
          </span>
          <span style={{ color: '#5e6c84' }}>
            {capitalizeFirstLetter(item.attribute)},{' '}
          </span>
          <span style={{ 
            color: item.attribute_impact > 0 
              ? colors.positiveGreen
              : item.attribute_impact < 0 
                ? colors.negativeRed 
                : '#5e6c84', 
            fontWeight: 600 
          }}>
            {formatImpactValue(item, options)}
          </span>
          {i < items.length - 1 && (
            <span style={{ margin: '0 8px', opacity: 0.6 }}>|</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// Styled component for text with conditional color and hover effect - Common implementation
export const CommentText = styled('span')(({ sentiment }) => ({
  color: sentiment === 'positive' ? colors.positiveGreen : colors.negativeRed,
  cursor: 'pointer',
  display: 'inline',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: sentiment === 'positive' ? 'rgba(76, 175, 80, 0.08)' : 'rgba(244, 67, 54, 0.08)'
  }
}));

export default {
  NoMaxWidthTooltip,
  TooltipContent,
  CommentText
}; 